import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Card,
  Cluster,
  EmptyState,
  Field,
  Input,
  Page,
  QueryState,
  RankedBars,
  Select,
  Stack,
  StatStrip,
  LinkIcon,
  TagIcon,
  TrendIcon,
  TargetIcon,
} from '@integra/ui';
import {
  useCampaigns,
  useConnectorConfigs,
  useCreateTrackedLink,
  useDeleteTrackedLink,
  useDestinations,
  useTrackedLinks,
} from '../api/hooks.js';
import type { TrackedLink } from '../api/types.js';

/**
 * Attribution & UTM (§20.5, Optimise). Build a tracked short link with UTM tags,
 * attribute it to a campaign or channel, and every click becomes a MEASURED
 * signal that flows into Performance, the daily trend series and the weekly
 * review — attribution read through the same provenance-honest measurement layer
 * as everything else (§2.7), never an invented number.
 */
export function AttributionPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const links = useTrackedLinks(workspaceId);
  const create = useCreateTrackedLink(workspaceId);
  const remove = useDeleteTrackedLink(workspaceId);
  const campaigns = useCampaigns(workspaceId);
  const destinations = useDestinations(workspaceId);
  const configs = useConnectorConfigs(workspaceId);

  const [targetUrl, setTargetUrl] = useState('');
  const [label, setLabel] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [destinationId, setDestinationId] = useState('');
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmContent, setUtmContent] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [created, setCreated] = useState<TrackedLink | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const campaignList = campaigns.data?.data ?? [];
  const destinationList = destinations.data?.data ?? [];
  const configName = (id: string): string =>
    configs.data?.data.find((c) => c.id === id)?.displayName ?? id;

  const nz = (v: string): string | null => (v.trim() === '' ? null : v.trim());

  function submit(): void {
    if (targetUrl.trim() === '' || label.trim() === '') return;
    create.mutate(
      {
        targetUrl: targetUrl.trim(),
        label: label.trim(),
        utm: {
          source: nz(utmSource),
          medium: nz(utmMedium),
          campaign: nz(utmCampaign),
          content: nz(utmContent),
          term: nz(utmTerm),
        },
        campaignId: nz(campaignId),
        assetId: null,
        destinationId: nz(destinationId),
      },
      {
        onSuccess: (res) => {
          setCreated(res.data);
          setTargetUrl('');
          setLabel('');
          setUtmContent('');
          setUtmTerm('');
        },
      },
    );
  }

  function copy(url: string): void {
    void navigator.clipboard?.writeText(url);
    setCopied(url);
  }

  const list = links.data?.data ?? [];
  const referrers = links.data?.referrers ?? [];
  const totalClicks = list.reduce((sum, l) => sum + l.clicks, 0);

  return (
    <Page
      title="Attribution & UTM"
      icon={LinkIcon}
      description="Build tracked short links with UTM tags so every click is measured and attributed to its campaign or channel."
      stats={
        list.length > 0 ? (
          <StatStrip
            stats={[
              { icon: LinkIcon, label: 'Tracked links', value: list.length },
              { icon: TrendIcon, label: 'Clicks measured', value: totalClicks, tone: 'accent' },
            ]}
          />
        ) : undefined
      }
    >
      <Stack gap="lg">
        <Card
          title="Build a tracked link"
          subtitle="A short link that appends your UTM tags and counts every click as a measured signal."
        >
          <Stack gap="md">
            <Cluster gap="md" align="start">
              <Field label="Destination URL" htmlFor="link-target">
                <Input
                  id="link-target"
                  value={targetUrl}
                  placeholder="https://example.com/landing"
                  onChange={(e) => setTargetUrl(e.target.value)}
                />
              </Field>
              <Field label="Label" htmlFor="link-label">
                <Input
                  id="link-label"
                  value={label}
                  placeholder="Spring launch — LinkedIn"
                  onChange={(e) => setLabel(e.target.value)}
                />
              </Field>
            </Cluster>

            <Cluster gap="md" align="start">
              <Field label="Attribute to campaign" htmlFor="link-campaign">
                <Select
                  id="link-campaign"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                >
                  <option value="">None</option>
                  {campaignList.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Attribute to channel" htmlFor="link-destination">
                <Select
                  id="link-destination"
                  value={destinationId}
                  onChange={(e) => setDestinationId(e.target.value)}
                >
                  <option value="">None</option>
                  {destinationList.map((d) => (
                    <option key={d.id} value={d.id}>
                      {configName(d.connectorConfigId)} ({d.status})
                    </option>
                  ))}
                </Select>
              </Field>
            </Cluster>

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                UTM parameters
              </p>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                <Field label="utm_source" htmlFor="utm-source">
                  <Input
                    id="utm-source"
                    value={utmSource}
                    placeholder="linkedin"
                    onChange={(e) => setUtmSource(e.target.value)}
                  />
                </Field>
                <Field label="utm_medium" htmlFor="utm-medium">
                  <Input
                    id="utm-medium"
                    value={utmMedium}
                    placeholder="social"
                    onChange={(e) => setUtmMedium(e.target.value)}
                  />
                </Field>
                <Field label="utm_campaign" htmlFor="utm-campaign">
                  <Input
                    id="utm-campaign"
                    value={utmCampaign}
                    placeholder="spring_launch"
                    onChange={(e) => setUtmCampaign(e.target.value)}
                  />
                </Field>
                <Field label="utm_content" htmlFor="utm-content">
                  <Input
                    id="utm-content"
                    value={utmContent}
                    placeholder="hero_cta"
                    onChange={(e) => setUtmContent(e.target.value)}
                  />
                </Field>
                <Field label="utm_term" htmlFor="utm-term">
                  <Input
                    id="utm-term"
                    value={utmTerm}
                    placeholder="optional"
                    onChange={(e) => setUtmTerm(e.target.value)}
                  />
                </Field>
              </div>
            </div>

            <Cluster justify="end">
              <Button
                loading={create.isPending}
                disabled={targetUrl.trim() === '' || label.trim() === ''}
                onClick={submit}
              >
                Create tracked link
              </Button>
            </Cluster>

            {created !== null && (
              <Alert tone="success" title="Tracked link created">
                <Cluster gap="sm" align="center">
                  <span className="break-all font-mono text-xs text-navy-700">
                    {created.shortUrl}
                  </span>
                  <Button size="sm" variant="secondary" onClick={() => copy(created.shortUrl)}>
                    {copied === created.shortUrl ? 'Copied' : 'Copy'}
                  </Button>
                </Cluster>
              </Alert>
            )}
            {create.error instanceof Error && (
              <Alert tone="error" title="Could not create the link">
                {create.error.message}
              </Alert>
            )}
          </Stack>
        </Card>

        <QueryState loading={links.isLoading} error={links.error} what="tracked links">
          {list.length === 0 ? (
            <EmptyState
              icon={LinkIcon}
              title="No tracked links yet"
              description="Build one above. Its clicks will be measured and attributed to the campaign or channel you chose."
              features={[
                { icon: TagIcon, label: 'UTM tags appended automatically' },
                { icon: TargetIcon, label: 'Attributed to a campaign or channel' },
                { icon: TrendIcon, label: 'Every click flows into Performance' },
              ]}
            />
          ) : (
            <Stack gap="lg">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div className="rounded-md border border-edge bg-surface px-4 py-3.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Tracked links
                  </p>
                  <p className="mt-1.5 font-mono text-3xl font-semibold tabular-nums text-navy-700">
                    {list.length}
                  </p>
                </div>
                <div className="rounded-md border border-edge bg-surface px-4 py-3.5">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Clicks (measured)
                  </p>
                  <p className="mt-1.5 font-mono text-3xl font-semibold tabular-nums text-navy-700">
                    {totalClicks.toLocaleString()}
                  </p>
                </div>
              </div>

              <Card
                title="Top links by clicks"
                subtitle="Measured outbound clicks per tracked link."
              >
                <RankedBars
                  items={list.map((l) => ({
                    label: l.label,
                    value: l.clicks,
                    valueLabel: `${l.clicks.toLocaleString()} clicks`,
                  }))}
                />
              </Card>

              {referrers.length > 0 && (
                <Card
                  title="Where clicks come from"
                  subtitle="The referring host of each measured click."
                >
                  <RankedBars
                    items={referrers.map((r) => ({
                      label: r.host,
                      value: r.clicks,
                      valueLabel: `${r.clicks.toLocaleString()} clicks`,
                    }))}
                  />
                </Card>
              )}

              <Card title="All tracked links">
                <Stack gap="sm">
                  {list.map((l) => (
                    <div
                      key={l.id}
                      className="flex flex-wrap items-center gap-3 rounded-md border border-edge bg-surface px-3 py-2.5"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-navy-700">{l.label}</p>
                        <p className="truncate font-mono text-[11px] text-ink-muted">
                          {l.shortUrl} → {l.targetUrl}
                        </p>
                      </div>
                      <Badge tone="neutral">{l.clicks.toLocaleString()} clicks</Badge>
                      <Button size="sm" variant="secondary" onClick={() => copy(l.shortUrl)}>
                        {copied === l.shortUrl ? 'Copied' : 'Copy'}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        loading={remove.isPending && remove.variables === l.id}
                        onClick={() => remove.mutate(l.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  ))}
                </Stack>
              </Card>
            </Stack>
          )}
        </QueryState>
      </Stack>
    </Page>
  );
}
